/**
 * What this trimester is being asked to deliver — in two figures that must never become one.
 *
 * OFFICIAL is the Hiring Database: open and backlogged roles due by the end of the trimester,
 * frozen where a baseline has been taken. It is the number Tracking measures against and the number
 * a target is committed against.
 *
 * PROPOSED is demand that exists but is not in the HDB — roles raised through the Org Planning
 * Overview and not yet approved (the purple ones on the timeline). It is shown because "we are at
 * our limit, and eleven more are being asked for" is a different sentence from "we are at our
 * limit", and a capacity plan that cannot say the first one is planning for a trimester that has
 * already stopped being true.
 *
 * ⚠️ They are never added together. Approved-but-not-yet-in-the-HDB is a third, smaller figure,
 * shown for the same reason: it is committed demand the official number misses, and folding it in
 * would move what Tracking measures against.
 */
import type { StoredTarget } from '../../../types/taTracking';
import type { ProposedDemand, TrackingData } from '../../../types/taTracking';
import { KpiTile } from '../tracking/trackingUi';

export function DemandStrip({
  tracking,
  committed,
  targetError,
}: {
  tracking: TrackingData;
  /**
   * ⚠️ The COMMITMENT COMES FROM ONE PLACE — this prop, the same state the commit card writes.
   * Reading `tracking.target.committed` here instead left the tile showing "—" after a successful
   * commit, because the tracking payload is only re-read on a reload.
   */
  committed: StoredTarget | null;
  targetError: string | null;
}) {
  const frozen = tracking.target.demandSource === 'baseline';
  const p: ProposedDemand | undefined = tracking.proposed;

  return (
    <>
      <div className="flex overflow-x-auto rounded-xl border border-gray-200 bg-white">
        <KpiTile
          testId="capacity-demand"
          label="Official demand"
          value={tracking.target.demand}
          note={frozen ? 'the frozen plan' : 'today’s table — nothing frozen'}
          tone={frozen ? 'neutral' : 'warn'}
          why="Open and backlogged roles in the Hiring Database due by the end of this trimester, including any already overdue. From the frozen baseline where one has been taken, otherwise today's table — which moves as the plan moves. This is the figure Tracking measures against."
        />
        <KpiTile
          testId="capacity-proposed"
          label="Proposed"
          value={p ? p.proposed : '—'}
          note={p?.error ? 'could not be read' : 'asked for, not approved'}
          tone={p?.error ? 'warn' : p && p.proposed > 0 ? 'warn' : 'neutral'}
          why="Roles raised through the Org Planning Overview that nobody has approved yet — the purple ones on the hiring capacity timeline. Counted for this trimester by their planned start month. Deliberately NOT added to the official demand: it is demand to weigh, not demand to plan against."
        />
        <KpiTile
          testId="capacity-hired"
          label="Hired"
          value={tracking.hired}
          note="offers accepted"
          why="Roles whose offer was accepted inside this trimester. Counted on the offer date, not the start date — a hire is won when it is signed."
        />
        <KpiTile
          testId="capacity-to-recruit"
          label="Still to recruit"
          value={tracking.toRecruit}
          note={tracking.onHold > 0 ? `${tracking.onHold} more on hold` : undefined}
          why="Live HDB roles that are open or backlogged and due by the end of this trimester. Excludes on-hold roles, which are paused on purpose and re-dated when recruiting resumes."
        />
        <KpiTile
          testId="capacity-committed"
          label="Committed target"
          value={committed ? committed.target : '—'}
          note={
            committed
              ? `by ${committed.committedBy.split('@')[0]}`
              : targetError
                ? 'could not be read'
                : 'not set for this trimester'
          }
          tone={committed ? 'ok' : targetError ? 'warn' : 'neutral'}
          why="What TA has undertaken to deliver this trimester. Set at the bottom of this tab; the Tracking tab measures progress against it. Deliberately a separate number from both demand and capacity."
        />
      </div>

      {/*
        ⚠️ LINES, not blocks. Role Prioritisation records the same lesson: stacked warning blocks
        pushed the figures people came for off the first screen, so a data notice collapses to a
        count and opens on demand.
      */}
      {p && (p.approved > 0 || p.undated.length > 0 || p.error) ? (
        <details className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-1.5 text-xs text-gray-700">
          <summary className="cursor-pointer">
            {p.error
              ? 'Proposed roles could not be read'
              : [
                  p.approved > 0 ? `${p.approved} approved but not in the HDB` : null,
                  p.undated.length > 0 ? `${p.undated.length} proposed without a date` : null,
                ]
                  .filter(Boolean)
                  .join(' · ')}
          </summary>
          {p.error ? (
            <p className="mt-1.5">{p.error}</p>
          ) : (
            <>
              {p.approved > 0 ? (
                <p className="mt-1.5">
                  <b className="font-semibold">{p.approved}</b> proposed{' '}
                  {p.approved === 1 ? 'role has' : 'roles have'} been approved but{' '}
                  {p.approved === 1 ? 'is' : 'are'} not yet a row in the Hiring Database, so the
                  official demand above does not include{' '}
                  {p.approved === 1 ? 'it' : 'them'}. Raise {p.approved === 1 ? 'it' : 'them'} in the
                  HDB and the figure catches up.
                </p>
              ) : null}
              {p.undated.length > 0 ? (
                <p className="mt-1.5">
                  Counted in no trimester, because nothing says when they start:{' '}
                  {p.undated.map((u) => u.title).join(', ')}.
                </p>
              ) : null}
            </>
          )}
        </details>
      ) : null}

      {tracking.attribution.unresolved.length > 0 && (
        <details className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs text-amber-900">
          <summary className="cursor-pointer">
            {tracking.attribution.unresolved.length} role
            {tracking.attribution.unresolved.length === 1 ? '' : 's'} in the demand above{' '}
            {tracking.attribution.unresolved.length === 1 ? 'belongs' : 'belong'} to no cluster
          </summary>
          <p className="mt-1.5 text-amber-800">
            Counted in the company demand and in no cluster’s bar — a guessed cluster would move the
            figure that cluster’s lead is judged on. Set <b>People Team</b> on the row in Airtable,
            or add the recruiter to the cluster map.
          </p>
          <ul className="mt-1.5 space-y-0.5">
            {tracking.attribution.unresolved.map((r) => (
              <li key={`${r.positionId}-${r.title}`}>
                <b className="font-semibold">{r.title}</b>
                <span className="text-amber-700"> — {r.why}</span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </>
  );
}
